import { ProposalData } from './types'

export { existingProposalFixture } from './existingProposalFixture'
export { freshProposalFixture } from './freshProposalFixture'

export function createPostUploadFixture(base: ProposalData): ProposalData {
  function updateArtifact(
    categories: typeof base.artifactCategories,
    artifactId: string,
    changes: Partial<(typeof base.artifactCategories)[0]['artifacts'][0]>,
  ) {
    return categories.map((cat) => ({
      ...cat,
      artifacts: cat.artifacts.map((a) =>
        a.id === artifactId ? { ...a, ...changes } : a,
      ),
      completedCount: cat.artifacts.filter((a) =>
        a.id === artifactId
          ? (changes.readyStatus ?? a.readyStatus)
          : a.readyStatus,
      ).length,
    }))
  }

  function updateCriterion(
    criteria: typeof base.criteria,
    criterionId: string,
    changes: Partial<(typeof base.criteria)[0]>,
  ) {
    return criteria.map((c) =>
      c.id === criterionId ? { ...c, ...changes } : c,
    )
  }

  let updatedCategories = base.artifactCategories

  updatedCategories = updateArtifact(updatedCategories, 'A-03-ProjectSummary', {
    hasFile: true,
    complianceStatus: 'satisfied',
    readyStatus: false,
    compliance: [
      {
        label: 'Length is limited to 30 lines of text.',
        passed: true,
        sourceUrls: [
          'https://grants.nih.gov/grants-process/write-application/how-to-apply-application-guide/page-limits',
        ],
      },
      {
        label: 'NIH PDF Attachment Formatting',
        passed: true,
        isChecklistRef: true,
        checklistName: 'NIH PDF Attachment Formatting',
      },
    ],
  })

  const nihAimsChecks = [
    { label: 'Document is provided as a single PDF file.', passed: true },
    { label: 'Paper size is US Letter (8.5" × 11").', passed: true },
    { label: 'Margins are at least 0.5 inches on all sides.', passed: true },
    {
      label: 'No text, figures, or other content appears in the margins.',
      passed: true,
    },
    { label: 'Font family is an NIH-accepted font.', passed: true },
    { label: 'Font color is black.', passed: true },
    { label: 'Font size is 11 pt or larger.', passed: true },
    {
      label: 'Type density is no more than 15 characters per inch.',
      passed: true,
    },
    {
      label: 'Line spacing is no more than 6 lines per vertical inch.',
      passed: false,
    },
    { label: 'No headers or footers are included.', passed: true },
    {
      label: 'File name is ≤ 50 characters and uses only standard characters.',
      passed: true,
    },
  ]

  updatedCategories = updateArtifact(updatedCategories, 'A-10-SpecificAims', {
    hasFile: true,
    complianceStatus: 'not-satisfied',
    readyStatus: false,
    compliance: [
      {
        label: 'Page limit: 1 page maximum.',
        passed: true,
        sourceUrls: [
          'https://grants.nih.gov/grants-process/write-application/how-to-apply-application-guide/page-limits',
        ],
      },
      {
        label: 'NIH PDF Attachment Formatting',
        passed: false,
        isChecklistRef: true,
        checklistName: 'NIH PDF Attachment Formatting',
        checklistOverrides: nihAimsChecks,
      },
    ],
  })

  const nihStrategyChecks = [
    { label: 'Document is provided as a single PDF file.', passed: true },
    { label: 'Paper size is US Letter (8.5" × 11").', passed: true },
    { label: 'Margins are at least 0.5 inches on all sides.', passed: true },
    {
      label: 'No text, figures, or other content appears in the margins.',
      passed: true,
    },
    { label: 'Font family is an NIH-accepted font.', passed: true },
    { label: 'Font color is black.', passed: true },
    { label: 'Font size is 11 pt or larger.', passed: false },
    {
      label: 'Type density is no more than 15 characters per inch.',
      passed: false,
    },
    {
      label: 'Line spacing is no more than 6 lines per vertical inch.',
      passed: false,
    },
    { label: 'No headers or footers are included.', passed: true },
    {
      label: 'File name is ≤ 50 characters and uses only standard characters.',
      passed: true,
    },
  ]

  updatedCategories = updateArtifact(
    updatedCategories,
    'A-11-ResearchStrategy',
    {
      hasFile: true,
      complianceStatus: 'not-satisfied',
      readyStatus: false,
      compliance: [
        {
          label: 'Page limit: 6 pages maximum for R21 Research Strategy.',
          passed: true,
          sourceUrls: [
            'https://grants.nih.gov/grants-process/write-application/how-to-apply-application-guide/page-limits',
          ],
        },
        {
          label: 'NIH PDF Attachment Formatting',
          passed: false,
          isChecklistRef: true,
          checklistName: 'NIH PDF Attachment Formatting',
          checklistOverrides: nihStrategyChecks,
        },
      ],
    },
  )

  let updatedCriteria = base.criteria
  updatedCriteria = updateCriterion(updatedCriteria, 'C-SCOPE-01', {
    status: 'satisfied',
    notes: 'The project is basic behavioral multisensory research.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-SCOPE-02', {
    status: 'satisfied',
    notes:
      'The project studies multisensory processing using both visual and auditory modalities.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-SCOPE-AOI-01', {
    status: 'satisfied',
    notes:
      'The proposal addresses FOA interest areas including lifespan changes in multisensory integration.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-SCOPE-03', {
    status: 'satisfied',
    notes: 'The project is responsive to NIH b-BSSR.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-DQ-01', {
    status: 'satisfied',
    notes: 'The project uses audiovisual tasks.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-DQ-02', {
    status: 'satisfied',
    notes: 'The project does not focus on pain perception.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-DQ-03', {
    status: 'satisfied',
    notes:
      'The proposal focuses on behavior and perception rather than neural circuits.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-DQ-04', {
    status: 'not-satisfied',
    notes:
      'The proposal is framed in applied/public-health terms, suggesting out-of-scope applied intent.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-ELIG-01', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-ELIG-02', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-ELIG-03', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-01', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-02', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-03', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-04', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-05', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-06', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-REV-07', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-IMPL-01', {
    status: 'unknown',
    notes: 'Not evaluated.',
  })
  updatedCriteria = updateCriterion(updatedCriteria, 'C-IMPL-02', {
    status: 'satisfied',
    notes:
      'The proposal is well-aligned with R21 exploratory/developmental intent.',
  })

  const updatedGrantsmanship: typeof base.grantsmanshipSections = [
    {
      id: 'structure',
      name: 'Structure',
      iconName: 'LayoutList',
      summary:
        'Across the available sections, the application\u2019s structure is generally easy to follow. The most consequential structural concerns center on feasibility signaling: the Research Strategy devotes substantial space to background relative to concrete execution details, and it lacks an explicit 2-year R21 timeline with milestones or decision points. The aims have a notable dependency (Aim 2 relying on Aim 1 outputs) without clear contingencies.',
      findings: [
        {
          id: 'gs-s1',
          text: 'Research Strategy is background-heavy and lacks benchmarked, decision-driven execution and analytic detail.',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'gs-s2',
          text: 'Key dependencies lack contingencies (recruitment, 2-hr battery tolerance, and Aim 2 go/no-go based on Aim 1 data quality/sample size).',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'gs-s3',
          text: 'No explicit 2-year R21 timeline, sequencing, or milestones/go/no-go criteria are provided for Aims 1\u20132.',
          artifacts: ['Research Strategy'],
        },
        {
          id: 'gs-s4',
          text: 'Specific Aim 2 depends on Aim 1 outcomes, reducing aim independence and creating a sequential plan without clear contingency if Aim 1 is null.',
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
      ],
    },
    {
      id: 'narrative',
      name: 'Narrative',
      iconName: 'BookOpen',
      summary:
        'The narrative is cohesive and internally consistent, with aligned terminology, stable scope, and a clear throughline. The Specific Aims are well matched to the described methods, and the exploratory/developmental framing is explicit and well positioned for an R21 mechanism.',
      findings: [],
    },
    {
      id: 'clarity',
      name: 'Clarity',
      iconName: 'ScanEye',
      summary:
        'The application is generally understandable and avoids empty rhetorical language. However, clarity is reduced by frequent long sentences, heavy acronym density including undefined terms, and inconsistent terminology for key constructs. The title also reads as generic and vague.',
      findings: [
        {
          id: 'gs-c1',
          text: 'Key constructs are labeled inconsistently (e.g., \u201Cmultisensory integration/function/processes\u201D), creating ambiguity about what is being measured across aims.',
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
        {
          id: 'gs-c2',
          text: 'Multiple sections contain frequent >50-word sentences, increasing reviewer cognitive load and reducing readability.',
          artifacts: [
            'Project Summary / Abstract',
            'Specific Aims',
            'Research Strategy',
          ],
        },
        {
          id: 'gs-c3',
          text: 'Acronym use is excessive and includes at least one undefined term (e.g., \u201CTD\u201D), reducing clarity for non-specialist reviewers.',
          artifacts: ['Specific Aims', 'Research Strategy'],
        },
        {
          id: 'gs-c4',
          text: 'Title is too generic; it does not specify the population/system, multisensory focus, methods, or primary outcome.',
          artifacts: ['Title'],
        },
      ],
    },
  ]

  const updatedHeader: typeof base.header = {
    ...base.header,
    proposalStatus: 'In Progress',
    overallProgress: 17,
    riskStatus: 'on-track',
    riskSummary: 'AI analysis complete. Awaiting user review.',
    riskSignals: [],
  }

  return {
    ...base,
    header: updatedHeader,
    artifactCategories: updatedCategories,
    criteria: updatedCriteria,
    grantsmanshipSections: updatedGrantsmanship,
    tasks: [],
  }
}
